import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { SafeModeProvider } from "./contexts/SafeModeContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { ProductDateCacheProvider } from "./contexts/ProductDateCacheContext";
import { FeatureFlagsProvider } from "./hooks/useFeatureFlags";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import FeaturesPage from "./pages/FeaturesPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import OnboardingPage from "./pages/OnboardingPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import DashboardPage from "./pages/DashboardPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import NewProductPage from "./pages/NewProductPage";
import NewProductWizardPage from "./pages/NewProductWizardPage";
import SimpleProductForm from "./pages/SimpleProductForm";
import SettingsPage from "./pages/SettingsPage";
import AdminPage from "./pages/AdminPage";
import AdminRolesPage from "./pages/AdminRolesPage";
import AdminTestingPage from "./pages/AdminTestingPage";
import AdminBillingAuditPage from "./pages/AdminBillingAuditPage";
import AdminCustomersPage from "./pages/AdminCustomersPage";
import AdminFeaturesPage from "./pages/AdminFeaturesPage";
import AdminFreeTierPage from "./pages/AdminFreeTierPage";
import SystemSettingsPage from "./pages/SystemSettingsPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import PlanManagementPage from "./pages/PlanManagementPage";
import TokenPricingPage from "./pages/TokenPricingPage";
import AIPromptsPage from "./pages/AIPromptsPage";
import AdminApiKeysPage from "./pages/AdminApiKeysPage";
import MCPTestHarnessPage from "./pages/MCPTestHarnessPage";
import CollaborationCanvas from "./pages/CollaborationCanvas";
import StackRankingSpace from "./pages/StackRankingSpace";
import StackRankingPublicVote from "./pages/StackRankingPublicVote";
import SupportFormPublic from "./pages/SupportFormPublic";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import GuestJoinPage from "./pages/GuestJoinPage";
import InviteClaimPage from "./pages/InviteClaimPage";
import ExecutiveAIPage from "./pages/ExecutiveAIPage";
import HelpPage from "./pages/HelpPage";
import DemoLandingPage from "./pages/DemoLandingPage";
import DemoManagementPage from "./pages/DemoManagementPage";
import MarketIntelligencePage from "./pages/MarketIntelligencePage";
import VisualizationsPage from "./pages/VisualizationsPage";
import ExplorerSignupPage from "./pages/ExplorerSignupPage";
import VisualizationWorkspace from "./pages/VisualizationWorkspace";
import VisualizationPublicView from "./pages/VisualizationPublicView";
import DemoBanner from "./components/demo/DemoBanner";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import FreeProductRedirect from "./components/layout/FreeProductRedirect";
import OrgSelectPage from "./pages/OrgSelectPage";
import MicrosoftAuthCallback from "./pages/MicrosoftAuthCallback";
import PricingPage from "./pages/PricingPage";
import ContactSalesPage from "./pages/ContactSalesPage";
import AskPage from "./pages/AskPage";
import SignupPricingPage from "./pages/SignupPricingPage";
import SignupSuccessPage from "./pages/SignupSuccessPage";
import SignupCancelPage from "./pages/SignupCancelPage";

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <FeatureFlagsProvider>
          <PermissionsProvider>
          <SafeModeProvider>
          <ProductDateCacheProvider>
          <SidebarProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/demo" element={<DemoLandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/explorer" element={<ExplorerSignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact-sales" element={<ContactSalesPage />} />
            <Route path="/ask" element={<AskPage />} />
            <Route path="/signup/pricing" element={<SignupPricingPage />} />
            <Route path="/signup/success" element={<SignupSuccessPage />} />
            <Route path="/signup/cancel" element={<SignupCancelPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
            <Route path="/auth/microsoft/callback" element={<MicrosoftAuthCallback />} />
            <Route path="/org-select" element={<OrgSelectPage />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/create-org"
              element={<OnboardingPage createOrgMode={true} />}
            />
            <Route path="/invite/:token" element={<InviteAcceptPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <FreeProductRedirect>
                    <DashboardPage />
                  </FreeProductRedirect>
                </ProtectedRoute>
              }
            />
            <Route
              path="/executive-ai"
              element={
                <ProtectedRoute>
                  <ExecutiveAIPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <FreeProductRedirect>
                    <ProductsPage />
                  </FreeProductRedirect>
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/new"
              element={
                <ProtectedRoute>
                  <NewProductWizardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/new-legacy"
              element={
                <ProtectedRoute>
                  <NewProductPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/new-simple"
              element={
                <ProtectedRoute>
                  <SimpleProductForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:productId"
              element={
                <ProtectedRoute>
                  <ProductDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <HelpPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help/:slug"
              element={
                <ProtectedRoute>
                  <HelpPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={<Navigate to="/admin/customers?tab=users" replace />}
            />
            <Route
              path="/admin/roles"
              element={
                <AdminProtectedRoute>
                  <AdminRolesPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/features"
              element={
                <AdminProtectedRoute>
                  <AdminFeaturesPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/free-tier"
              element={
                <AdminProtectedRoute>
                  <AdminFreeTierPage />
                </AdminProtectedRoute>
              }
            />
            {/* New Customers page (combines Organizations, Users, Billing) */}
            <Route
              path="/admin/customers"
              element={
                <AdminProtectedRoute>
                  <AdminCustomersPage />
                </AdminProtectedRoute>
              }
            />
            {/* Legacy routes - redirect to new Customers page */}
            <Route
              path="/admin/organizations"
              element={<Navigate to="/admin/customers?tab=organizations" replace />}
            />
            <Route
              path="/admin/billing"
              element={<Navigate to="/admin/customers" replace />}
            />
            <Route
              path="/admin/billing/:orgId"
              element={<Navigate to="/admin/customers" replace />}
            />
            <Route
              path="/admin/testing"
              element={
                <AdminProtectedRoute>
                  <AdminTestingPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminProtectedRoute>
                  <SystemSettingsPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/email"
              element={
                <AdminProtectedRoute>
                  <EmailSettingsPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/plans"
              element={
                <AdminProtectedRoute>
                  <PlanManagementPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/token-pricing"
              element={
                <AdminProtectedRoute>
                  <TokenPricingPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/mcp"
              element={
                <AdminProtectedRoute>
                  <MCPTestHarnessPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/ai-prompts"
              element={
                <AdminProtectedRoute>
                  <AIPromptsPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/api-keys"
              element={
                <AdminProtectedRoute>
                  <AdminApiKeysPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/demo"
              element={
                <AdminProtectedRoute>
                  <DemoManagementPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/market-intel"
              element={
                <AdminProtectedRoute>
                  <MarketIntelligencePage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/collaboration/:spaceId"
              element={<CollaborationCanvas />}
            />
            <Route path="/collaboration/invite/:token" element={<InviteClaimPage />} />
            <Route path="/collaboration/join/:token" element={<GuestJoinPage />} />
            <Route
              path="/stack-ranking/:spaceId"
              element={
                <ProtectedRoute>
                  <StackRankingSpace />
                </ProtectedRoute>
              }
            />
            <Route path="/stack-ranking/vote/:shareToken" element={<StackRankingPublicVote />} />
            <Route path="/support/:shareToken" element={<SupportFormPublic />} />
            <Route path="/support/verify/:token" element={<SupportFormPublic />} />
            <Route path="/viz/:shareToken" element={<VisualizationPublicView />} />
            <Route
              path="/visualizations"
              element={
                <ProtectedRoute>
                  <VisualizationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/visualizations/:visualizationId"
              element={
                <ProtectedRoute>
                  <VisualizationWorkspace />
                </ProtectedRoute>
              }
            />
            </Routes>
            <Toaster />
          </SidebarProvider>
          </ProductDateCacheProvider>
          </SafeModeProvider>
          </PermissionsProvider>
          </FeatureFlagsProvider>
        </AuthProvider>
        <DemoBanner />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
